/**
 * Master Property Code Use Cases
 * Shared lookup for the searchable propcode dropdowns.
 */

import { formatCodeLabel } from "@/utils/formatCodeLabel";
import type { TSearchableOption } from "@/ui/molecules";

/**
 * The slice of the API client this use case needs, described structurally so
 * callers can pass `api.propertyCodes` without dragging in the whole client type.
 */
export type PropertyCodeSearchApi = {
    listPaginated: (params?: {
        search?: string;
        page?: number;
        page_size?: number;
        active_only?: boolean;
    }) => Promise<{ items: Array<{ code: string; description: string | null }> }>;
};

/**
 * How many matches one dropdown page shows. This bounds the rendered list, not
 * what is reachable: the query runs server-side over the whole master set, so
 * narrowing the text reaches codes beyond this page. Contrast the old eager
 * `list({ page_size: 1000 })` preload, where anything past the first 1000 codes
 * (ordered by code) could not be found at all.
 */
const SEARCH_PAGE_SIZE = 200;

/**
 * Search the company's active master property codes, server-side, matching the
 * query against code OR property name. An empty query returns the first page so
 * the dropdown is browsable before the user types.
 *
 * Labels read `CODE - Description` (or just `CODE` when there is no description).
 * A dropdown's already-selected value seeds its resting label from the bare code,
 * since a stored mapping carries the code alone.
 */
export async function searchMasterPropertyCodes(
    api: PropertyCodeSearchApi,
    query: string,
): Promise<TSearchableOption[]> {
    const trimmed = query.trim();
    const response = await api.listPaginated({
        search: trimmed || undefined,
        active_only: true,
        page_size: SEARCH_PAGE_SIZE,
    });
    return response.items.map((p) => ({
        value: p.code,
        label: formatCodeLabel(p.code, p.description),
    }));
}
